import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  useServerContext,
  useOffers,
  useUserData,
  useNoomSummit,
} from "src/hooks/redux";
import { NOOM_SUMMIT_PRODUCT_ID } from "src/utils/constants";
import {
  updateShippingInfoError,
  updateShippingInfo,
  updateShippingStatus,
  updatePurchasedPlans,
} from "src/utils/redux/slices/offers";
import {
  Offer,
  ServerContextState,
} from "src/utils/redux/slices/serverContext";
import { isNoomSummitDisplayableInStore } from "src/utils/userSegment/features";

export const getSingleOffers = (serverContext: ServerContextState) => {
  const { offers } = serverContext;

  if (!offers) return [];
  return offers["hw-offers-baseline"]?.single || [];
};

const getStoreOffers = async (
  serverContext: ServerContextState,
  purchasedNoomSummit: boolean
) => {
  const { offers } = serverContext;

  if (!offers) return [];
  const allOffers = offers["hw-offers-baseline"]?.store || [];

  // Hide Noom Summit from users who have already purchased it or it is not eligible for
  await isNoomSummitDisplayableInStore.load?.();
  if (!isNoomSummitDisplayableInStore() || purchasedNoomSummit) {
    return allOffers.filter(
      (offer) => offer.addon_product_id !== NOOM_SUMMIT_PRODUCT_ID
    );
  }

  return allOffers;
};

export const useOffersContext = () => {
  const serverContext = useServerContext();
  const userData = useUserData();
  const { purchasedNoomSummit } = useNoomSummit();

  const singleOffers = getSingleOffers(serverContext);

  const [offers, setOffers] = useState<Offer[]>([]);
  useEffect(() => {
    getStoreOffers(serverContext, purchasedNoomSummit).then(setOffers);
  }, [serverContext, purchasedNoomSummit]);

  const dispatch = useDispatch();

  const setShippingInfo = useCallback(
    (shippingInfo) => {
      dispatch(updateShippingInfo(shippingInfo));
    },
    [dispatch]
  );

  const setShippingInfoError = (shippingInfoError) => {
    dispatch(updateShippingInfoError(shippingInfoError));
  };

  const setShippingStatus = (shippingStatus) => {
    dispatch(updateShippingStatus(shippingStatus));
  };

  const setPurchasedPlans = (purchasedPlans) => {
    dispatch(updatePurchasedPlans(purchasedPlans));
  };

  const { shippingInfo, shippingStatus, shippingInfoError, purchasedPlans } =
    useOffers();

  const name = userData?.name;
  useEffect(() => {
    if (name) {
      const nameSplit = name.split(" ");
      const firstName = nameSplit[0];
      const lastName = nameSplit.slice(1).join(" ");

      setShippingInfo({
        ...shippingInfo,
        firstName,
        lastName,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, setShippingInfo]);

  return {
    offers,
    singleOffers,
    shippingInfo,
    setShippingInfo,
    shippingInfoError,
    setShippingInfoError,
    shippingStatus,
    setShippingStatus,
    setPurchasedPlans,
    purchasedPlans,
  };
};
